import { useRef } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useDismiss } from "../../lib/useDismiss.js";
import { useI18n } from "../../lib/useI18n.js";
import { useSettingsStore } from "../../stores/index.js";
import type { SidebarPart } from "../../stores/settings.store.js";

interface Props {
  part: SidebarPart;
  label: string;
  x: number;
  y: number;
  onClose: () => void;
}

export function SidebarContextMenu({ part, label, x, y, onClose }: Props) {
  const { t } = useI18n();
  const setSidebarVisible = useSettingsStore((s) => s.setSidebarVisible);
  const navigate = useNavigate();
  const menuRef = useRef<HTMLDivElement | null>(null);
  useDismiss(onClose, { capture: true }, menuRef);

  const style: React.CSSProperties = {
    position: "fixed",
    left: Math.min(x, window.innerWidth - 220),
    top: Math.min(y, window.innerHeight - 120),
    zIndex: 9999,
  };

  return createPortal(
    <div
      ref={menuRef}
      style={style}
      className="w-52 rounded-md border bg-card/90 text-card-foreground shadow-lg backdrop-blur-sm"
      onContextMenu={(e) => e.preventDefault()}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex flex-col py-1">
        <button
          type="button"
          onClick={() => { onClose(); void setSidebarVisible(part, false); }}
          className="px-3 py-2 text-left text-sm hover:bg-accent/80 hover:text-accent-foreground"
        >
          {t("sidebar.hide", { label })}
        </button>
        <button
          type="button"
          onClick={() => { onClose(); navigate("/settings/navigation"); }}
          className="px-3 py-2 text-left text-sm hover:bg-accent/80 hover:text-accent-foreground"
        >
          {t("sidebar.settings")}
        </button>
      </div>
    </div>,
    document.body,
  );
}
